/**
 * Onda Tempestade v2 — the storm as ART DIRECTION, not as a particle count (client/render-only, ZERO wire,
 * ZERO sim impact; the authoritative intensity still arrives in the snapshot header and only ever drives what
 * this file DRAWS).
 *
 * The old storm was "procedural sem graça": fat white rain rectangles, one flat grey tint, a PointLight that
 * blinked, and an open-ended cylinder for the Eye that read as a giant hollow cone hanging in the sky. The
 * rebuild layers the storm the way a matte painter would, cheapest-first, so a phone still holds the budget:
 *
 *   1. RAIN, near field — one Points draw call, but each drop is a thin WIND-SLANTED streak (the streak angle
 *      is the real wind direction projected into view space, so rain lays over when the gale does), with soft
 *      ends, distance fade and a lens-plane fade so nothing pops in your face.
 *   2. RAIN CURTAINS, mid/far — the thing that actually makes a sea look stormy: translucent sheets of falling
 *      water marching downwind between you and the horizon. ONE instanced draw call for all of them, procedural
 *      fbm alpha (no texture), aerial-perspective blended into the fog colour so they sit in the depth.
 *   3. SPINDRIFT — at full fury the wind tears the crests apart: a low, fast, near-horizontal spray layer that
 *      rides the real ocean height function. One Points draw call, only above SPUME_START.
 *   4. LIGHTNING — no THREE.PointLight (see lights.ts: the FakeLight law). A strike is a scene-wide FLASH value
 *      this module owns: a multi-stab envelope (stab → dip → second stab → tail) that the renderer folds into
 *      sun/hemi/sky/ocean/cloud in one place, plus an optional real BOLT (a camera-facing forked ribbon built
 *      on the CPU for that strike only, ~60 tris, one draw call) and a DISTANCE-DELAYED thunder cue.
 *   5. WATERSPOUT — the storm's landmark at peak fury: a bending, noise-scrolled funnel with a spray skirt at
 *      the sea and a top that dissolves into the cloud deck. Not a smooth cone: the radius profile has a waist,
 *      the whole column sways, the alpha is silhouette-weighted so it reads as VOLUME.
 *   6. THE EYE — rebuilt (this is the "cone gigante ao avesso com um vão no meio" the owner reported): a calm
 *      pool with a foam rim, a towering EYEWALL curtain around it, and a soft god-ray shaft whose alpha is
 *      driven by view silhouette + vertical falloff + noise, so it never shows a hard geometric edge again.
 *
 * Everything fades continuously off the smoothed intensity (`Renderer.setStorm`) — no element ever pops in.
 * Per-frame work is allocation-free (scratch vectors, typed arrays, no closures in the hot path).
 */
import * as THREE from 'three';
import { EYE, STORM } from '@piratas/protocol';
import { oceanHeightAt } from '@piratas/sim';

/* ------------------------------------------------------------------------------------------------------ *
 *  TUNING — every knob the storm's LOOK hangs on. Render-only, so it lives here next to the code that uses
 *  it (the shared/gameplay storm constants stay in @piratas/protocol STORM: cycle, wind, wave scale, fog).
 * ------------------------------------------------------------------------------------------------------ */
export const STORM_ART = {
  /** Near-field rain: max streaks, half-size of the box that rides the camera (m), ceiling, fall speed. */
  RAIN_N: 3000,
  RAIN_AREA: 62,
  RAIN_TOP: 78,
  RAIN_FALL: 58,
  /** Screen size (px at dpr 1) of a rain streak, and how hard the gale lays it over (× wind strength). */
  RAIN_SIZE: 7,
  RAIN_SLANT: 0.75,
  /** Mid-distance rain curtains: count, ring radii (m), sheet size (m), and the intensity they ramp over. */
  CURTAIN_N: 14,
  CURTAIN_NEAR: 110,
  CURTAIN_FAR: 380,
  CURTAIN_W: 150,
  CURTAIN_H: 150,
  CURTAIN_START: 0.28,
  CURTAIN_FULL: 0.75,
  CURTAIN_OPACITY: 0.95,
  /** Spindrift (wind-torn crest spray): count, ramp-in intensity, drift speed (m/s at full storm). */
  SPUME_N: 420,
  SPUME_START: 0.5,
  SPUME_SPEED: 26,
  SPUME_AREA: 70,
  /** Lightning: flash length (s), the intensity a bolt (rather than a bare sheet flash) becomes possible at,
   *  and the chance a strike draws a visible bolt. Gaps come from STORM.LIGHTNING_MIN_GAP. */
  FLASH_S: 0.75,
  BOLT_START: 0.5,
  BOLT_CHANCE: 0.62,
  /** Speed of sound in "sea-of-robin" metres/second — the thunder rolls in this long after the flash. */
  THUNDER_SPEED: 210,
  /** Waterspout: intensity it forms at, fade time (s), distance from the camera (m), height (m). */
  SPOUT_START: 0.76,
  SPOUT_FADE_S: 7,
  SPOUT_DIST: 380,
  SPOUT_H: 190,
  SPOUT_R: 15,
  /** Lens spatter: intensity it starts / saturates at, and its peak strength. Deliberately faint —
   *  it is the difference between "there is rain in the world" and "I am OUT IN IT", and the moment it
   *  reads as an effect instead of as water it has failed. */
  LENS_START: 0.34,
  LENS_FULL: 0.85,
  LENS_STRENGTH: 0.22,
} as const;

/* ------------------------------------------------------------------------------------------------------ *
 *  PURE CORE (unit-tested in storm.test.ts — no WebGL, no THREE state)
 * ------------------------------------------------------------------------------------------------------ */

/** 0..1 LOD ramp between two intensity thresholds (the shape every storm layer fades in on). */
export function ramp(v: number, start: number, full: number): number {
  return Math.max(0, Math.min(1, (v - start) / Math.max(1e-3, full - start)));
}

/** Near-field rain amount for a storm intensity (count + opacity LOD), 0 below STORM.RAIN_START. */
export function rainAmount(storm: number): number { return ramp(storm, STORM.RAIN_START, STORM.RAIN_FULL); }
/** Mid-distance curtain amount. */
export function curtainAmount(storm: number): number { return ramp(storm, STORM_ART.CURTAIN_START, STORM_ART.CURTAIN_FULL); }
/** Wind-torn spindrift amount (only at real fury). */
export function spumeAmount(storm: number): number { return ramp(storm, STORM_ART.SPUME_START, 1); }
/** Lens-spatter amount — the only layer that lives in SCREEN space rather than the world. */
export function lensAmount(storm: number): number { return ramp(storm, STORM_ART.LENS_START, STORM_ART.LENS_FULL); }

/**
 * Lightning flash envelope, 0..1, `age` seconds after the strike. A real CG flash is not one ramp: it stabs,
 * dips, stabs again (the return strokes) and decays. `seed` (0..1) shifts the strobe so no two strikes flicker
 * the same. Pure + bounded — the renderer multiplies sun/hemi/sky/ocean by this single number.
 */
export function flashEnvelope(age: number, seed = 0, dur = STORM_ART.FLASH_S): number {
  if (!(age >= 0) || age > dur) return 0;
  const x = age / dur;
  const decay = Math.exp(-x * 3.4);                                   // overall fall-off
  const strobe = 0.70 + 0.30 * Math.sin((x * (26 + seed * 14)) + seed * 6.283); // return strokes
  const attack = Math.min(1, x / 0.035);                              // 20 ms rise — sharp, never a click
  return Math.max(0, Math.min(1, decay * strobe * attack));
}

/** Seconds until the next strike at this intensity — fiercer storm, faster lightning. `r` is 0..1 random. */
export function strikeGap(storm: number, r: number): number {
  return (STORM.LIGHTNING_MIN_GAP / Math.max(0.35, storm)) * (0.55 + r * 1.25);
}

/** Seconds between the flash and its thunder for a strike this far away (m). Distance you can HEAR. */
export function thunderDelay(dist: number): number { return Math.min(6, dist / STORM_ART.THUNDER_SPEED); }

/**
 * Deterministic forked bolt spine in a LOCAL frame: y from `top` down to ~0, x/z jittered, returned as a flat
 * [x,y,z, …] list plus the index where each fork starts. Pure (its own LCG) so a test can assert the shape.
 */
export function boltPath(seed: number, top: number, segs = 14): { pts: Float32Array; forkAt: number } {
  let s = (seed | 0) || 1;
  const rnd = (): number => { s = (s * 1664525 + 1013904223) | 0; return ((s >>> 8) & 0xffff) / 0xffff; };
  const n = segs + 1;
  const pts = new Float32Array(n * 3);
  let x = 0, z = 0;
  const drift = (rnd() - 0.5) * 0.5;
  for (let i = 0; i < n; i++) {
    const f = i / segs;
    pts[i * 3] = x; pts[i * 3 + 1] = top * (1 - f); pts[i * 3 + 2] = z;
    const step = top / segs;
    x += (rnd() - 0.5) * step * 0.55 + drift * step * 0.25;
    z += (rnd() - 0.5) * step * 0.34;
  }
  return { pts, forkAt: 3 + ((seed >>> 3) % Math.max(1, segs - 6)) };
}

/* ------------------------------------------------------------------------------------------------------ *
 *  Shared GLSL: one cheap value-noise kit every storm shader draws its structure from (no textures at all —
 *  the boot budget stays untouched).
 * ------------------------------------------------------------------------------------------------------ */
/**
 * PRECISION-SAFE CELL HASH — and it is not a style preference, it is a mobile blocker.
 *
 * The classic fract(sin(dot(p, k)) * 43758.5453) needs the product to keep a fractional part. On a GPU that
 * honours mediump as real fp16 (most phones), 43758.5 already lands where the float spacing is 32, so the
 * fraction is IDENTICALLY ZERO: every lattice cell hashes to the same number and every noise built on it
 * collapses to a constant. The rain curtains lose their veil, the spindrift its speckle, the waterspout its
 * shear and the Eye its churn — the storm flattens into painted sheets, on exactly the devices the game is
 * built for. (Photographed once already, on the lens spatter, which is why the lens carries this same hash.)
 *
 * The form below keeps every intermediate inside ~[0, 100), so there is always a fraction left to take, and
 * it degrades gracefully instead of falling off a cliff. Every fragment that includes this kit is also
 * compiled highp (see each header), so on a device that HAS highp it is exact as well.
 */
const NOISE_GLSL = /* glsl */ `
  float hash21(vec2 p){
    vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
    q += dot(q, q.yzx + 33.33);
    return fract((q.x + q.y) * q.z);
  }
  float vnoise(vec2 p){
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0)), c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float fbm2(vec2 p){ return vnoise(p) * 0.66 + vnoise(p * 2.13 + 4.7) * 0.34; }
  float fbm3(vec2 p){ return vnoise(p) * 0.53 + vnoise(p * 2.03 + 3.1) * 0.30 + vnoise(p * 4.17 + 9.2) * 0.17; }
`;

/** Everything the storm needs to know about the world this frame (all read-only; no allocation). */
export interface StormEnv {
  t: number;              // render clock (s)
  dt: number;             // frame delta (s)
  storm: number;          // 0..1 smoothed authoritative intensity
  night: number;          // 0 day … 1 deep night
  camera: THREE.Camera;
  windDir: number;        // world radians (x east, z = sim-y south)
  windStrength: number;
  waveScale: number;      // storm swell amp (so spray rides the SAME sea the ocean shader draws)
  fog: THREE.Color;       // current horizon/fog colour — every layer blends into it (aerial perspective)
  quality: 'low' | 'medium' | 'high';
}

export class StormFx {
  /* ---- near-field rain ---- */
  private rain: THREE.Points;
  private rainMat: THREE.ShaderMaterial;
  private rainPos: Float32Array;
  private rainVel: Float32Array;
  /* ---- mid-distance curtains ---- */
  private curtains: THREE.InstancedMesh;
  private curtainMat: THREE.ShaderMaterial;
  private curtainPos: Float32Array;   // world centre per sheet (x, y0, z)
  private curtainSize: Float32Array;  // width, height, seed
  /* ---- spindrift ---- */
  private spume: THREE.Points;
  private spumeMat: THREE.ShaderMaterial;
  private spumePos: Float32Array;
  private spumeAge: Float32Array;
  /* ---- lightning ---- */
  private bolt: THREE.Mesh;
  private boltMat: THREE.ShaderMaterial;
  private boltPos: Float32Array;
  private boltAlpha: Float32Array;
  private _flash = 0;
  private _flashSeed = 0;
  private _flashT = -99;
  private _nextStrike = 4;
  private _flashAz = 0;          // world azimuth of the current strike (clouds glow on that side)
  private _forceAz: number | undefined;  // dev hook: pin the next strike's azimuth (render proof)
  private _thunderAt = -1;       // scheduled thunder time (s) — distance-delayed
  private _thunderQueued = false;
  private _thunderDist = 400;    // how far away that strike was (m) — drives clap loudness + camera shake
  /* ---- waterspout ---- */
  private spout: THREE.Group;
  private spoutMat: THREE.ShaderMaterial;
  private spoutSkirtMat: THREE.ShaderMaterial;
  private spoutAmt = 0;
  private spoutBearing = 1.1;
  /* ---- lens spatter (screen space) ---- */
  private lens: THREE.Mesh;
  private lensMat: THREE.ShaderMaterial;
  /* ---- the Eye ---- */
  private eye: EyeFx | null = null;
  private eyePos: { x: number; y: number } | null = null;
  private scene: THREE.Scene;
  /* ---- scratch ---- */
  private readonly _v = new THREE.Vector3();
  private readonly _c = new THREE.Color();
  private _quality: 'low' | 'medium' | 'high' = 'medium';

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    // ---- rain ----
    {
      const N = STORM_ART.RAIN_N;
      const pos = new Float32Array(N * 3), vel = new Float32Array(N), seed = new Float32Array(N);
      for (let i = 0; i < N; i++) {
        pos[i * 3] = (Math.random() * 2 - 1) * STORM_ART.RAIN_AREA;
        pos[i * 3 + 1] = Math.random() * STORM_ART.RAIN_TOP;
        pos[i * 3 + 2] = (Math.random() * 2 - 1) * STORM_ART.RAIN_AREA;
        vel[i] = STORM_ART.RAIN_FALL * (0.78 + Math.random() * 0.55);
        seed[i] = Math.random();
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
      g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
      this.rainMat = new THREE.ShaderMaterial({
        uniforms: {
          uColor: { value: new THREE.Color(0xdceaf6) }, uOpacity: { value: 0 },
          uSlant: { value: 0 }, uSize: { value: STORM_ART.RAIN_SIZE }, uPix: { value: 1 },
        },
        vertexShader: /* glsl */ `
          attribute float aSeed;
          uniform float uSize, uPix;
          varying float vFade, vSeed;
          void main(){
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            float d = -mv.z;
            gl_Position = projectionMatrix * mv;
            // near streaks are long, far ones shrink to a glint; never below ~1 px so distant rain still reads
            gl_PointSize = uSize * uPix * (0.8 + 13.0 / max(6.0, d)) * (0.7 + aSeed * 0.6);
            // fade OUT far away (the curtains take over) and right at the lens (no drop smearing the camera)
            vFade = smoothstep(155.0, 45.0, d) * smoothstep(1.6, 8.0, d);
            vSeed = aSeed;
          }
        `,
        fragmentShader: /* glsl */ `
          precision mediump float;
          uniform vec3 uColor; uniform float uOpacity, uSlant;
          varying float vFade, vSeed;
          void main(){
            vec2 p = gl_PointCoord - 0.5;
            float c = cos(uSlant), s = sin(uSlant);
            vec2 q = vec2(p.x * c - p.y * s, p.x * s + p.y * c);   // rotate the sprite into the wind
            float core = smoothstep(0.055, 0.006, abs(q.x));        // a HAIRLINE, not a fat white pill
            float len  = smoothstep(0.5, 0.34, abs(q.y));            // long streak, soft head + tail
            float a = core * len * uOpacity * vFade * (0.45 + vSeed * 0.75);
            if (a < 0.008) discard;
            gl_FragColor = vec4(pow(uColor, vec3(2.2)), a);
          }
        `,
        transparent: true, depthWrite: false, fog: false,
      });
      this.rain = new THREE.Points(g, this.rainMat);
      this.rain.frustumCulled = false; this.rain.renderOrder = 4; this.rain.visible = false;
      this.rainPos = pos; this.rainVel = vel;
      scene.add(this.rain);
    }
    // ---- rain curtains (instanced, cylindrically billboarded sheets) ----
    {
      const N = STORM_ART.CURTAIN_N;
      const geo = new THREE.PlaneGeometry(1, 1);
      const aPos = new Float32Array(N * 3), aSize = new Float32Array(N * 3);
      for (let i = 0; i < N; i++) { aSize[i * 3] = STORM_ART.CURTAIN_W; aSize[i * 3 + 1] = STORM_ART.CURTAIN_H; aSize[i * 3 + 2] = Math.random() * 10; }
      geo.setAttribute('aPos', new THREE.InstancedBufferAttribute(aPos, 3).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('aSize', new THREE.InstancedBufferAttribute(aSize, 3));
      this.curtainMat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 }, uOpacity: { value: 0 }, uCam: { value: new THREE.Vector3() },
          uColor: { value: new THREE.Color(0x9fb4c4) }, uFog: { value: new THREE.Color(0x8ec5e6) }, uFlash: { value: 0 },
        },
        vertexShader: /* glsl */ `
          attribute vec3 aPos, aSize;
          uniform vec3 uCam;
          varying vec2 vUv; varying float vSeed, vDist;
          void main(){
            vec3 c = aPos;
            vec3 toCam = vec3(uCam.x - c.x, 0.0, uCam.z - c.z);
            float L = max(0.001, length(toCam));
            vec3 right = vec3(-toCam.z, 0.0, toCam.x) / L;         // cylindrical billboard: always vertical
            vec3 world = c + right * (position.x * aSize.x) + vec3(0.0, (position.y + 0.5) * aSize.y, 0.0);
            vUv = position.xy + 0.5;
            vSeed = aSize.z; vDist = L;
            gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          precision highp float;   // includes NOISE_GLSL — the cell hash must not be evaluated in fp16
          uniform float uTime, uOpacity, uFlash;
          uniform vec3 uColor, uFog;
          varying vec2 vUv; varying float vSeed, vDist;
          ${NOISE_GLSL}
          void main(){
            // two scrolling scales: a slow VEIL (the body of the shaft) + fast vertical STREAKS (falling water)
            float veil   = fbm2(vec2(vUv.x * 2.6 + vSeed * 5.0, vUv.y * 1.5 - uTime * 0.42));
            float streak = vnoise(vec2(vUv.x * 26.0 + vSeed * 3.0, vUv.y * 0.9 - uTime * 1.9));
            float a = veil * 1.15 + streak * 0.55 - 0.32;
            a *= smoothstep(0.0, 0.26, vUv.x) * smoothstep(1.0, 0.74, vUv.x);   // soft sides — no rectangle edge
            a *= smoothstep(0.0, 0.30, vUv.y) * smoothstep(1.0, 0.62, vUv.y);   // dissolves into spray below, cloud above
            a = max(0.0, a) * uOpacity;
            if (a < 0.004) discard;
            vec3 col = mix(uColor, uFog, clamp(vDist / 520.0, 0.0, 0.85));      // aerial perspective
            col += vec3(0.30, 0.34, 0.42) * uFlash;                             // the sheet lights up on a strike
            gl_FragColor = vec4(pow(col, vec3(2.2)), a);
          }
        `,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      });
      this.curtains = new THREE.InstancedMesh(geo, this.curtainMat, N);
      this.curtains.frustumCulled = false; this.curtains.renderOrder = 3; this.curtains.visible = false;
      this.curtains.instanceMatrix.setUsage(THREE.StaticDrawUsage); // identity — the shader places every sheet
      const idm = new THREE.Matrix4();
      for (let i = 0; i < N; i++) this.curtains.setMatrixAt(i, idm);
      this.curtainPos = aPos; this.curtainSize = aSize;
      scene.add(this.curtains);
    }
    // ---- spindrift ----
    {
      const N = STORM_ART.SPUME_N;
      const pos = new Float32Array(N * 3), age = new Float32Array(N), seed = new Float32Array(N);
      for (let i = 0; i < N; i++) { age[i] = Math.random() * 2.4; seed[i] = Math.random(); }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
      g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
      this.spumeMat = new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color(0xeaf3fa) }, uOpacity: { value: 0 }, uSlant: { value: 1.2 }, uPix: { value: 1 } },
        vertexShader: /* glsl */ `
          attribute float aSeed;
          uniform float uPix;
          varying float vFade, vSeed;
          void main(){
            vec4 mv = modelViewMatrix * vec4(position, 1.0);
            float d = -mv.z;
            gl_Position = projectionMatrix * mv;
            gl_PointSize = (2.4 + aSeed * 3.4) * uPix * (0.9 + 12.0 / max(8.0, d));
            vFade = smoothstep(150.0, 30.0, d) * smoothstep(2.0, 10.0, d);
            vSeed = aSeed;
          }
        `,
        fragmentShader: /* glsl */ `
          precision mediump float;
          uniform vec3 uColor; uniform float uOpacity, uSlant;
          varying float vFade, vSeed;
          void main(){
            vec2 p = gl_PointCoord - 0.5;
            float c = cos(uSlant), s = sin(uSlant);
            vec2 q = vec2(p.x * c - p.y * s, p.x * s + p.y * c);
            float a = smoothstep(0.45, 0.0, length(vec2(q.x * 0.30, q.y * 1.8)));   // a torn, wind-stretched wisp
            a *= uOpacity * vFade * (0.5 + vSeed * 0.7);
            if (a < 0.008) discard;
            gl_FragColor = vec4(pow(uColor, vec3(2.2)), a * 0.30);
          }
        `,
        transparent: true, depthWrite: false, fog: false,
      });
      this.spume = new THREE.Points(g, this.spumeMat);
      this.spume.frustumCulled = false; this.spume.renderOrder = 4; this.spume.visible = false;
      this.spumePos = pos; this.spumeAge = age;
      scene.add(this.spume);
    }
    // ---- lightning bolt (one reused ribbon: core + halo, rebuilt on the CPU per strike) ----
    {
      const SEG = 15, VERTS = SEG * 2 * 2 * 2; // (core + halo) × (spine + fork) × 2 verts per step … generous
      const pos = new Float32Array(VERTS * 3), alpha = new Float32Array(VERTS);
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
      g.setAttribute('aAlpha', new THREE.BufferAttribute(alpha, 1).setUsage(THREE.DynamicDrawUsage));
      this.boltMat = new THREE.ShaderMaterial({
        uniforms: { uColor: { value: new THREE.Color(0xdfe9ff) }, uOpacity: { value: 0 } },
        vertexShader: 'attribute float aAlpha; varying float vA; void main(){ vA = aAlpha; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
        fragmentShader: 'precision mediump float; varying float vA; uniform vec3 uColor; uniform float uOpacity; void main(){ float a = vA * uOpacity; if (a <= 0.004) discard; gl_FragColor = vec4(pow(uColor, vec3(2.2)) * (0.9 + 2.6 * a), a); }',
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: false,
      });
      this.bolt = new THREE.Mesh(g, this.boltMat);
      this.bolt.frustumCulled = false; this.bolt.renderOrder = 5; this.bolt.visible = false;
      this.boltPos = pos; this.boltAlpha = alpha;
      scene.add(this.bolt);
    }
    // ---- waterspout ----
    {
      const g = new THREE.Group();
      const funnel = new THREE.CylinderGeometry(1, 1, 1, 26, 14, true); // unit tube — the shader shapes it
      this.spoutMat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 }, uAmt: { value: 0 }, uH: { value: STORM_ART.SPOUT_H }, uR: { value: STORM_ART.SPOUT_R },
          uColor: { value: new THREE.Color(0x59626d) }, uFog: { value: new THREE.Color(0x8ec5e6) }, uFlash: { value: 0 },
        },
        vertexShader: /* glsl */ `
          uniform float uTime, uH, uR, uAmt;
          varying vec2 vUv; varying vec3 vN; varying vec3 vW;
          void main(){
            vUv = uv;                                    // uv.y: 0 bottom … 1 top of the tube
            float f = uv.y;
            // radius profile: wide bell where it meets the cloud, a pinched WAIST, a flared foot at the sea
            float prof = 0.42 + 1.55 * pow(f, 2.6) + 0.55 * pow(1.0 - f, 3.0);
            // the whole column leans + snakes (this is what a cone can never do)
            float sway = sin(uTime * 0.55 + f * 3.1) * (2.0 + 9.0 * f) + sin(uTime * 0.23 + f * 1.7) * 6.0 * f;
            float sway2 = cos(uTime * 0.47 + f * 2.6) * (1.5 + 6.0 * f);
            vec3 p = vec3(position.x * uR * prof + sway, (f - 0.5) * uH, position.z * uR * prof + sway2);
            vN = normalize(vec3(position.x, 0.0, position.z));
            vec4 wp = modelMatrix * vec4(p, 1.0);
            vW = wp.xyz;
            gl_Position = projectionMatrix * viewMatrix * wp;
          }
        `,
        fragmentShader: /* glsl */ `
          precision highp float;   // uTime is shared with the vertex stage — precision must match
          uniform float uTime, uAmt, uFlash;
          uniform vec3 uColor, uFog;
          varying vec2 vUv; varying vec3 vN; varying vec3 vW;
          ${NOISE_GLSL}
          void main(){
            // spiralling shear: the noise rotates around the column AND climbs it
            vec2 q = vec2(vUv.x * 6.0 + vUv.y * 2.4 + uTime * 0.22, vUv.y * 3.4 - uTime * 0.95);
            float n = fbm3(q);
            float tear = vnoise(vec2(vUv.x * 22.0 - uTime * 1.4, vUv.y * 7.0 - uTime * 2.6)); // shredded edges
            float body = smoothstep(0.18, 0.55, n * 0.85 + tear * 0.35);
            // vertical shaping: dissolves INTO the cloud at the top, boils into spray at the foot
            body *= smoothstep(1.0, 0.82, vUv.y) * smoothstep(0.0, 0.025, vUv.y);
            // silhouette weighting -> reads as volume, never as a hard-edged cone shell
            vec3 v = normalize(cameraPosition - vW);
            float rim = 1.0 - abs(dot(normalize(vN), v));
            float a = body * (0.85 + 0.5 * rim) * uAmt;
            if (a < 0.006) discard;
            float dist = length(cameraPosition - vW);
            vec3 base = mix(uColor * (0.85 + 0.35 * vUv.y), vec3(0.86, 0.90, 0.94), smoothstep(0.22, 0.0, vUv.y) * 0.75);
            vec3 col = mix(base, uFog, clamp(dist / 1400.0, 0.0, 0.55));
            col += vec3(0.28, 0.32, 0.4) * uFlash;
            gl_FragColor = vec4(pow(col, vec3(2.2)), min(1.0, a * 1.45));
          }
        `,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      });
      const funnelMesh = new THREE.Mesh(funnel, this.spoutMat);
      funnelMesh.position.y = STORM_ART.SPOUT_H * 0.5;
      funnelMesh.frustumCulled = false;
      g.add(funnelMesh);
      // spray skirt: the sea being ripped up around the foot (a flat disc of boiling mist)
      this.spoutSkirtMat = new THREE.ShaderMaterial({
        uniforms: { uTime: { value: 0 }, uAmt: { value: 0 }, uColor: { value: new THREE.Color(0xe6eef4) }, uFog: { value: new THREE.Color(0x8ec5e6) } },
        vertexShader: 'varying vec2 vUv; varying vec3 vW; void main(){ vUv = uv; vec4 wp = modelMatrix * vec4(position, 1.0); vW = wp.xyz; gl_Position = projectionMatrix * viewMatrix * wp; }',
        fragmentShader: /* glsl */ `
          precision highp float;   // includes NOISE_GLSL — the cell hash must not be evaluated in fp16
          uniform float uTime, uAmt; uniform vec3 uColor, uFog;
          varying vec2 vUv; varying vec3 vW;
          ${NOISE_GLSL}
          void main(){
            vec2 p = (vUv - 0.5) * 2.0;
            float r = length(p);
            if (r > 1.0) discard;
            float ang = atan(p.y, p.x);
            float swirl = fbm2(vec2(ang * 2.2 + uTime * 1.1 + r * 3.0, r * 4.0 - uTime * 0.7));
            float a = smoothstep(1.0, 0.25, r) * smoothstep(0.0, 0.22, r) * swirl * uAmt;
            if (a < 0.005) discard;
            float dist = length(cameraPosition - vW);
            gl_FragColor = vec4(pow(mix(uColor, uFog, clamp(dist / 900.0, 0.0, 0.8)), vec3(2.2)), a * 0.75);
          }
        `,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
      });
      const skirt = new THREE.Mesh(new THREE.PlaneGeometry(STORM_ART.SPOUT_R * 9, STORM_ART.SPOUT_R * 9), this.spoutSkirtMat);
      skirt.rotation.x = -Math.PI / 2; skirt.position.y = 1.5; skirt.frustumCulled = false;
      g.add(skirt);
      g.visible = false;
      this.spout = g;
      scene.add(g);
    }
    // ---- lens spatter: rain ON THE CAMERA, the only layer in screen space ----
    {
      // The quad is emitted straight in clip space (no matrices at all), so it is one triangle-pair that
      // always covers the frame regardless of camera, fov or aspect — and it costs no CPU per frame.
      const g = new THREE.PlaneGeometry(2, 2);
      this.lensMat = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 }, uAmt: { value: 0 }, uAspect: { value: 1.78 },
          uTint: { value: new THREE.Color(0xdfe9f2) }, uFlash: { value: 0 },
        },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = position.xy * 0.5 + 0.5; gl_Position = vec4(position.xy, 0.0, 1.0); }',
        fragmentShader: /* glsl */ `
          // HIGHP, and it is not optional. The scatter hash below is a fract(sin(·) * BIG) — the classic —
          // and BIG is 43758.5. On a GPU that honours mediump as fp16 the product lands where the float
          // spacing is already 32, so its fractional part is IDENTICALLY ZERO: every cell draws the same
          // drop, at the same spot, at the same size, and the "spatter" renders as a hard 150 px grid of
          // beads stamped across the frame (photographed; it is what a real gale looked like). The two-tier
          // hash below keeps every intermediate small so it degrades gracefully, and highp makes it exact.
          precision highp float;
          uniform float uTime, uAmt, uAspect, uFlash;
          uniform vec3 uTint;
          varying vec2 vUv;
          /** Precision-safe cell hash: no intermediate ever leaves ~[0, 100), so there is always a
           *  fractional part left to take. Returns 0..1, decorrelated between neighbouring cells. */
          float lHash(vec2 p) {
            vec3 q = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
            q += dot(q, q.yzx + 33.33);
            return fract((q.x + q.y) * q.z);
          }
          /**
           * A drop clinging to glass is a LENS: it gathers light in the middle and goes dark at the rim
           * where the curvature turns the ray away. With no framebuffer copy to refract (that would cost a
           * render target and a full resolve — not on this budget) the read is faked with exactly that
           * signature: a bright core and a soft dark rim. It is enough, because the eye reads "beads on
           * glass" from the SHAPE and the rim, not from what is behind them.
           */
          float bead(vec2 q, float r) {
            float d = length(q) / max(r, 1e-4);
            if (d > 1.0) return 0.0;
            // The RIM used to be nearly as strong as the core, which turned every drop into a dark ring —
            // photographed, the frame read as bubbles pressed against the glass rather than as rain on a
            // lens. A real bead is mostly its bright gathered centre with only a hint of dark curvature.
            float core = smoothstep(0.80, 0.0, d);                             // the gathered highlight
            float rim  = smoothstep(0.55, 0.95, d) * smoothstep(1.0, 0.80, d); // the dark curvature edge
            return core * 0.85 - rim * 0.30;
          }
          /**
           * One hashed cell grid's worth of drops. The cells argument sets the density.
           *
           * THE DROP IS KEPT INSIDE ITS OWN CELL. The obvious way to scatter beads — hash a cell, put the
           * drop at a random spot in it, then evaluate fract(g) minus that centre — CLIPS every drop that
           * overhangs a cell boundary, turning it into a straight-edged rectangle. Over dark water you never notice; over a
           * bright sunlit sea it prints a quilt of white tiles across the frame (which is exactly what it
           * did). Inseting the centre by the radius costs one mix and makes the clip unreachable, and with
           * three sparse layers at different densities there is no grid left to read.
           */
          float layer(vec2 p, float cells, float rad, float density, float seed) {
            vec2 g = p * cells;
            vec2 id = floor(g);
            float h = lHash(id + seed), h2 = lHash(id + seed + 31.7), h3 = lHash(id + seed + 71.3);
            if (h3 > density) return 0.0;                        // sparse: most cells stay clear
            // each drop has its own life, so the pane fills and clears instead of pulsing as one
            float life = fract(uTime * (0.05 + h * 0.10) + h * 9.13);
            float born = smoothstep(0.0, 0.12, life) * smoothstep(1.0, 0.70, life);
            if (born < 0.01) return 0.0;
            float r = rad * (0.55 + h2 * 0.85);
            // a drop that has hung long enough RUNS: it slides down its cell and thins out
            float run = smoothstep(0.45, 1.0, life) * step(0.62, h2);
            vec2 c = vec2(mix(r, 1.0 - r, h), mix(1.0 - r, r, run * 0.9 + h2 * (1.0 - run) * 0.9));
            vec2 q = fract(g) - c;
            float acc = bead(q, r) * born;
            if (run > 0.02) {                                    // the thinning tail it leaves behind
              float up = q.y;
              float reach = run * 0.55;
              acc += smoothstep(r * 0.40, 0.0, abs(q.x)) * smoothstep(0.0, 0.04, up) * smoothstep(reach, 0.04, up) * 0.22 * born;
            }
            return acc;
          }
          void main(){
            if (uAmt < 0.004) discard;
            // aspect-correct so beads are round, not ovals, on any screen
            vec2 p = vec2(vUv.x * uAspect, vUv.y);
            // three scales: a few fat slow drops, a middle scatter, and a haze of fine mist. No particle
            // buffer, no CPU, no allocation — and stable frame to frame.
            // Two layers, not three: this is a FULLSCREEN pass, so every layer is paid on every pixel
            // of the frame. Two sparse scales already read as scattered beads and the third measured as
            // pure cost.
            // Radii are per-CELL fractions, so the fat layer's 0.26 meant beads a QUARTER of a 150 px cell
            // across — 40 px blobs, far bigger than rain ever is on a lens, and they dominated the frame.
            // Halved, and the sparse layer thinned: the spatter has to be something you notice you are
            // looking through, not something you look at.
            float acc = layer(p, 6.0, 0.13, 0.22, 0.0)
                      + layer(p, 15.0, 0.11, 0.34, 13.7) * 0.85;
            // the frame edges catch more water than the middle (the lens hood / your own brow shields it),
            // which is also what keeps the centre of the screen readable in a fight
            float edge = 0.35 + 0.65 * smoothstep(0.15, 0.62, length((vUv - 0.5) * vec2(1.15, 1.0)));
            float a = acc * uAmt * ${STORM_ART.LENS_STRENGTH.toFixed(3)} * edge;
            if (abs(a) < 0.004) discard;
            // lightning glares THROUGH the beads — the one moment the spatter is supposed to be obvious
            vec3 col = uTint * (1.0 + 2.2 * uFlash);
            gl_FragColor = vec4(pow(col, vec3(2.2)) * sign(a), abs(a));
          }
        `,
        transparent: true, depthTest: false, depthWrite: false, fog: false,
        blending: THREE.CustomBlending,
        // additive where the bead gathers light, subtractive where its rim bends light away — one pass,
        // and it never washes the frame out the way a plain white overlay does
        blendSrc: THREE.SrcAlphaFactor, blendDst: THREE.OneFactor, blendEquation: THREE.AddEquation,
      });
      this.lens = new THREE.Mesh(g, this.lensMat);
      this.lens.frustumCulled = false;
      this.lens.renderOrder = 9000;   // dead last: it is ON the lens, in front of everything
      this.lens.visible = false;
      scene.add(this.lens);
    }
  }

  /** Quality preset: Low drops the mid-distance curtains + spindrift (fill-rate, the mobile lever). */
  setQuality(q: 'low' | 'medium' | 'high'): void { this._quality = q; }

  /** Current lightning luminance 0..1 — the renderer folds it into sun/hemi/sky/ocean/clouds. */
  get flash(): number { return this._flash; }
  /** World azimuth (radians) of the current strike, so the cloud deck can glow on THAT side. */
  get flashAzimuth(): number { return this._flashAz; }
  /** True once per strike, when the thunder has TRAVELLED to the listener (consumed by main → SFX). */
  consumeThunder(): boolean { const q = this._thunderQueued; this._thunderQueued = false; return q; }
  /** Distance (m) of the strike whose thunder is arriving — near strikes crack and SHAKE, far ones roll. */
  get thunderDist(): number { return this._thunderDist; }

  /** Dev/E2E render-proof hook: fire the next strike NOW (lightning is otherwise on a random schedule, so a
   *  headless screenshot could never catch one), optionally on a given world azimuth so the bolt lands IN
   *  frame. Pure theatre — it drives nothing but the flash + bolt. */
  forceStrike(az?: number): void { this._nextStrike = -1; this._forceAz = az; }

  /** Show/hide the Eye of the Storm at a sim position (null = none). Rebuilds only when the spot moves. */
  setEye(pos: { x: number; y: number } | null): void {
    if (!pos) { if (this.eye) { this.eye.dispose(this.scene); this.eye = null; } this.eyePos = null; return; }
    if (this.eye && this.eyePos && this.eyePos.x === pos.x && this.eyePos.y === pos.y) return;
    if (this.eye) this.eye.dispose(this.scene);
    this.eye = new EyeFx(this.scene, pos.x, pos.y);
    this.eyePos = { x: pos.x, y: pos.y };
  }

  /** One call per rendered sea frame, AFTER the storm intensity has been eased. Allocation-free. */
  update(env: StormEnv): void {
    const { t, dt, storm: s, camera } = env;
    const cam = camera.position;
    const pix = Math.min(2, window.devicePixelRatio || 1);
    this.updateRain(env, cam, pix);
    this.updateCurtains(env, cam);
    this.updateSpume(env, cam, pix);
    this.updateLightning(t, dt, s, cam);
    this.updateSpout(env, cam);
    this.updateLens(env);
    this.eye?.update(t, env.night, s, env.fog);
  }

  /* ------------------------------------------ lens spatter ----------------------------------------- */
  private updateLens(env: StormEnv): void {
    // Low quality drops it entirely: it is a FULLSCREEN fill pass, which is exactly the cost a weak phone
    // cannot spare — and it is pure flavour, so nothing is lost but polish.
    const amt = this._quality === 'low' ? 0 : lensAmount(env.storm);
    if (amt <= 0.004) { this.lens.visible = false; return; }
    this.lens.visible = true;
    const u = this.lensMat.uniforms;
    u.uTime!.value = env.t;
    u.uAmt!.value = amt * (1 - env.night * 0.35);   // less to catch the light on a black night
    u.uFlash!.value = this._flash;
    const cam = env.camera as THREE.PerspectiveCamera;
    u.uAspect!.value = cam.aspect || 1.78;
  }

  /* ------------------------------------------------ rain ------------------------------------------------ */
  private updateRain(env: StormEnv, cam: THREE.Vector3, pix: number): void {
    const amt = rainAmount(env.storm);
    if (amt <= 0.002) { this.rain.visible = false; return; }
    this.rain.visible = true;
    const u = this.rainMat.uniforms;
    u.uOpacity!.value = 0.22 + 0.26 * amt;
    u.uPix!.value = pix;
    // The streak lies over with the REAL wind, and it lies over by exactly as much as the drop actually
    // DRIFTS: one slant factor drives both the horizontal advection below and the screen-space rotation of
    // the sprite (projected into view space), so the rain never looks pasted on.
    const k = STORM_ART.RAIN_SLANT * (0.5 + 0.5 * Math.min(1.6, env.windStrength)) * (0.35 + 0.65 * env.storm);
    this._v.set(Math.cos(env.windDir) * k, -1, Math.sin(env.windDir) * k).normalize();
    this._v.transformDirection(env.camera.matrixWorldInverse);
    u.uSlant!.value = Math.atan2(this._v.x, -this._v.y);
    // advect + recycle (client FX — Math.random is fine here, it never touches the sim)
    this.rain.position.set(cam.x, 0, cam.z);
    const arr = this.rainPos, vel = this.rainVel, A = STORM_ART.RAIN_AREA;
    const n = Math.max(1, ((this._quality === 'low' ? 0.45 : 1) * STORM_ART.RAIN_N * amt) | 0);
    const drift = STORM_ART.RAIN_FALL * k * env.dt;
    const wx = Math.cos(env.windDir) * drift, wz = Math.sin(env.windDir) * drift;
    for (let i = 0; i < n; i++) {
      const j = i * 3;
      let y = (arr[j + 1] ?? 0) - (vel[i] ?? STORM_ART.RAIN_FALL) * env.dt;
      let x = (arr[j] ?? 0) + wx, z = (arr[j + 2] ?? 0) + wz;
      if (y < -2 || x * x + z * z > A * A * 2.2) {
        x = (Math.random() * 2 - 1) * A; z = (Math.random() * 2 - 1) * A; y = STORM_ART.RAIN_TOP * (0.55 + Math.random() * 0.45);
      }
      arr[j] = x; arr[j + 1] = y; arr[j + 2] = z;
    }
    const attr = this.rain.geometry.getAttribute('position') as THREE.BufferAttribute;
    attr.needsUpdate = true;
    this.rain.geometry.setDrawRange(0, n);
  }

  /* ---------------------------------------------- curtains ---------------------------------------------- */
  private updateCurtains(env: StormEnv, cam: THREE.Vector3): void {
    const amt = this._quality === 'low' ? 0 : curtainAmount(env.storm);
    if (amt <= 0.002) { this.curtains.visible = false; return; }
    this.curtains.visible = true;
    const u = this.curtainMat.uniforms;
    u.uTime!.value = env.t;
    u.uOpacity!.value = STORM_ART.CURTAIN_OPACITY * amt * (1 - env.night * 0.45);
    u.uFlash!.value = this._flash;
    (u.uCam!.value as THREE.Vector3).copy(cam);
    (u.uColor!.value as THREE.Color).setRGB(0.42, 0.47, 0.53).lerp(this._c.setRGB(0.13, 0.16, 0.21), env.night * 0.8);
    (u.uFog!.value as THREE.Color).copy(env.fog);
    // drift downwind; a sheet that walks past the camera is respawned upwind on the far ring
    const p = this.curtainPos, sz = this.curtainSize, N = STORM_ART.CURTAIN_N;
    const dx = Math.cos(env.windDir), dz = Math.sin(env.windDir);
    const step = 11 * env.storm * env.dt;
    let dirty = false;
    for (let i = 0; i < N; i++) {
      const j = i * 3;
      let x = (p[j] ?? 0) + dx * step, z = (p[j + 2] ?? 0) + dz * step;
      const ddx = x - cam.x, ddz = z - cam.z, d2 = ddx * ddx + ddz * ddz;
      const FAR = STORM_ART.CURTAIN_FAR, NEAR = STORM_ART.CURTAIN_NEAR;
      if (d2 > FAR * FAR || d2 < NEAR * NEAR * 0.35 || (x === 0 && z === 0)) {
        // reseed UPWIND on the ring so sheets always march toward you
        const a = Math.atan2(-dz, -dx) + (Math.random() - 0.5) * 2.2;
        const r = NEAR + Math.random() * (FAR - NEAR);
        x = cam.x + Math.cos(a) * r; z = cam.z + Math.sin(a) * r;
        sz[j] = STORM_ART.CURTAIN_W * (0.55 + Math.random() * 0.9);
        sz[j + 1] = STORM_ART.CURTAIN_H * (0.7 + Math.random() * 0.7);
        sz[j + 2] = Math.random() * 10;
        (this.curtains.geometry.getAttribute('aSize') as THREE.BufferAttribute).needsUpdate = true;
      }
      p[j] = x; p[j + 1] = -6; p[j + 2] = z;
      dirty = true;
    }
    if (dirty) (this.curtains.geometry.getAttribute('aPos') as THREE.BufferAttribute).needsUpdate = true;
  }

  /* ---------------------------------------------- spindrift --------------------------------------------- */
  private updateSpume(env: StormEnv, cam: THREE.Vector3, pix: number): void {
    const amt = this._quality === 'low' ? 0 : spumeAmount(env.storm);
    if (amt <= 0.002) { this.spume.visible = false; return; }
    this.spume.visible = true;
    const u = this.spumeMat.uniforms;
    u.uOpacity!.value = 0.55 * amt;
    u.uPix!.value = pix;
    // spray blows almost flat: slant it ~70° off vertical in view space
    this._v.set(Math.cos(env.windDir), -0.22, Math.sin(env.windDir)).normalize().transformDirection(env.camera.matrixWorldInverse);
    u.uSlant!.value = Math.atan2(this._v.x, -this._v.y);
    const p = this.spumePos, age = this.spumeAge, N = STORM_ART.SPUME_N, A = STORM_ART.SPUME_AREA;
    const sp = STORM_ART.SPUME_SPEED * (0.5 + 0.5 * env.storm);
    const vx = Math.cos(env.windDir) * sp, vz = Math.sin(env.windDir) * sp;
    const n = (N * amt) | 0;
    for (let i = 0; i < n; i++) {
      const j = i * 3;
      let a = (age[i] ?? 0) + env.dt;
      let x = (p[j] ?? 0) + vx * env.dt, z = (p[j + 2] ?? 0) + vz * env.dt;
      const ddx = x - cam.x, ddz = z - cam.z;
      if (a > 2.6 || ddx * ddx + ddz * ddz > A * A * 2.5 || (x === 0 && z === 0)) {
        const ang = Math.random() * Math.PI * 2, r = Math.sqrt(Math.random()) * A;
        x = cam.x + Math.cos(ang) * r; z = cam.z + Math.sin(ang) * r; a = Math.random() * 0.6;
      }
      age[i] = a;
      // torn OFF a crest: it starts on the sea and is lofted a little as it flies
      p[j] = x; p[j + 1] = oceanHeightAt(x, z, env.t, env.waveScale) + 0.6 + a * 2.6; p[j + 2] = z;
    }
    (this.spume.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    this.spume.geometry.setDrawRange(0, n);
  }

  /* ---------------------------------------------- lightning --------------------------------------------- */
  private updateLightning(t: number, _dt: number, s: number, cam: THREE.Vector3): void {
    if (s > STORM.LIGHTNING_START && t >= this._nextStrike) {
      this._flashT = t;
      this._flashSeed = Math.random();
      this._flashAz = this._forceAz ?? Math.random() * Math.PI * 2;
      this._forceAz = undefined;
      const dist = 220 + Math.random() * 520;
      this._thunderDist = dist;
      this._thunderAt = t + thunderDelay(dist);
      this._nextStrike = t + strikeGap(s, Math.random());
      // a bolt only for the closer strikes at real fury — the rest are sheet flashes behind the cloud deck
      if (s > STORM_ART.BOLT_START && Math.random() < STORM_ART.BOLT_CHANCE) this.buildBolt(cam, this._flashAz, dist, this._flashSeed);
      else this.bolt.visible = false;
    }
    if (this._thunderAt > 0 && t >= this._thunderAt) { this._thunderQueued = true; this._thunderAt = -1; }
    this._flash = flashEnvelope(t - this._flashT, this._flashSeed) * Math.min(1, s * 1.15);
    if (this.bolt.visible) {
      // the bolt itself dies faster than the sky glow (the channel cools in ~150 ms)
      const age = t - this._flashT;
      const life = Math.max(0, 1 - age / 0.42);
      this.boltMat.uniforms.uOpacity!.value = life * life * (0.75 + 0.25 * Math.sin(age * 90));
      if (life <= 0) this.bolt.visible = false;
    }
  }

  /** Build the strike geometry: a camera-facing forked ribbon (core + soft halo) in world space. */
  private buildBolt(cam: THREE.Vector3, az: number, dist: number, seed: number): void {
    const top = 210 + seed * 90;
    const path = boltPath((seed * 1e6) | 0, top, 14);
    const bx = cam.x + Math.cos(az) * dist, bz = cam.z + Math.sin(az) * dist;
    // ribbon "right" = horizontal vector perpendicular to the view direction (the bolt always faces you)
    let rx = -(bz - cam.z), rz = (bx - cam.x);
    const rl = Math.hypot(rx, rz) || 1; rx /= rl; rz /= rl;
    const pos = this.boltPos, al = this.boltAlpha;
    const n = path.pts.length / 3;
    let v = 0;
    const emit = (px: number, py: number, pz: number, w: number, a: number): void => {
      if (v + 2 > al.length) return;
      pos[v * 3] = px - rx * w; pos[v * 3 + 1] = py; pos[v * 3 + 2] = pz - rz * w; al[v] = a; v++;
      pos[v * 3] = px + rx * w; pos[v * 3 + 1] = py; pos[v * 3 + 2] = pz + rz * w; al[v] = a; v++;
    };
    const strip: number[] = [];
    const layer = (w0: number, a0: number, from: number, to: number, ox: number, oz: number): void => {
      const start = v / 2;
      for (let i = from; i < to; i++) {
        const f = (i - from) / Math.max(1, to - from - 1);
        const px = bx + (path.pts[i * 3] ?? 0) + ox, py = path.pts[i * 3 + 1] ?? 0, pz = bz + (path.pts[i * 3 + 2] ?? 0) + oz;
        emit(px, py, pz, w0 * (1 - f * 0.55) * (dist / 380), a0 * (0.35 + 0.65 * (1 - f * 0.7)));
      }
      const count = v / 2 - start;
      for (let i = 0; i < count - 1; i++) {
        const a = (start + i) * 2, b = a + 2;
        strip.push(a, a + 1, b, a + 1, b + 1, b);
      }
    };
    layer(13.0, 0.22, 0, n, 0, 0);                                 // soft halo (the channel's glow)
    layer(3.0, 1.0, 0, n, 0, 0);                                   // hot core
    const fk = Math.min(n - 2, path.forkAt);
    layer(1.9, 0.75, fk, Math.min(n, fk + 6), (seed - 0.5) * 46, (seed - 0.3) * 30); // one fork
    for (let i = v; i < al.length; i++) al[i] = 0;
    const g = this.bolt.geometry;
    g.setIndex(strip);
    (g.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (g.getAttribute('aAlpha') as THREE.BufferAttribute).needsUpdate = true;
    this.bolt.visible = true;
    this.boltMat.uniforms.uOpacity!.value = 1;
  }

  /* ---------------------------------------------- waterspout -------------------------------------------- */
  private updateSpout(env: StormEnv, cam: THREE.Vector3): void {
    const want = env.storm > STORM_ART.SPOUT_START && this._quality !== 'low' ? 1 : 0;
    const rate = env.dt / STORM_ART.SPOUT_FADE_S;
    this.spoutAmt += Math.max(-rate, Math.min(rate, want - this.spoutAmt));
    if (this.spoutAmt <= 0.004) { this.spout.visible = false; return; }
    this.spout.visible = true;
    // it stands off in the storm: a fixed bearing that drifts with the wind, always at SPOUT_DIST — the
    // landmark you can SEE from the whole sea but never sail into (zero gameplay, pure spectacle).
    this.spoutBearing += env.dt * 0.012;
    const az = this.spoutBearing + env.windDir * 0.15;
    this.spout.position.set(cam.x + Math.cos(az) * STORM_ART.SPOUT_DIST, 0, cam.z + Math.sin(az) * STORM_ART.SPOUT_DIST);
    const u = this.spoutMat.uniforms;
    u.uTime!.value = env.t; u.uAmt!.value = this.spoutAmt; u.uFlash!.value = this._flash;
    (u.uFog!.value as THREE.Color).copy(env.fog);
    (u.uColor!.value as THREE.Color).setRGB(0.35, 0.39, 0.44).lerp(this._c.setRGB(0.11, 0.13, 0.17), env.night * 0.85);
    const su = this.spoutSkirtMat.uniforms;
    su.uTime!.value = env.t; su.uAmt!.value = this.spoutAmt * 1.6;
    (su.uFog!.value as THREE.Color).copy(env.fog);
  }

  /** Free every GPU resource this system owns (sea → port teardown). */
  dispose(scene: THREE.Scene): void {
    for (const o of [this.rain, this.curtains, this.spume, this.bolt, this.lens] as THREE.Object3D[]) scene.remove(o);
    scene.remove(this.spout);
    this.rain.geometry.dispose(); this.rainMat.dispose();
    this.curtains.geometry.dispose(); this.curtainMat.dispose(); this.curtains.dispose();
    this.spume.geometry.dispose(); this.spumeMat.dispose();
    this.bolt.geometry.dispose(); this.boltMat.dispose();
    this.lens.geometry.dispose(); this.lensMat.dispose();
    this.spout.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    this.spoutMat.dispose(); this.spoutSkirtMat.dispose();
    this.eye?.dispose(scene); this.eye = null;
  }
}

/* ------------------------------------------------------------------------------------------------------ *
 *  THE EYE OF THE STORM — rebuilt. The old build was a single open-ended cylinder with a flat additive
 *  colour: from the sea it read as a GIANT HOLLOW CONE with a gap in the middle (the owner's report). The
 *  new one is three cooperating layers, all soft-edged: the calm pool + foam rim on the water, the towering
 *  eyewall curtain around it, and a god-ray shaft whose alpha comes from the view silhouette (so it has no
 *  edge of its own at all). 3 draw calls, and only while an eye is actually open.
 * ------------------------------------------------------------------------------------------------------ */
class EyeFx {
  private group = new THREE.Group();
  private poolMat: THREE.ShaderMaterial;
  private wallMat: THREE.ShaderMaterial;
  private shaftMat: THREE.ShaderMaterial;

  constructor(scene: THREE.Scene, x: number, z: number) {
    const R = EYE.RADIUS; // the calm pocket the SERVER grants inside — the art must sit exactly on it
    // --- the calm pool: a still, luminous disc with a churned foam rim ---
    this.poolMat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uAmt: { value: 1 }, uCalm: { value: new THREE.Color(0x69d8e8) }, uFoam: { value: new THREE.Color(0xdff4ff) }, uFog: { value: new THREE.Color(0x8ec5e6) } },
      vertexShader: 'varying vec2 vUv; varying vec3 vW; void main(){ vUv = uv; vec4 wp = modelMatrix * vec4(position, 1.0); vW = wp.xyz; gl_Position = projectionMatrix * viewMatrix * wp; }',
      fragmentShader: /* glsl */ `
        precision highp float;   // includes NOISE_GLSL — the cell hash must not be evaluated in fp16
        uniform float uTime, uAmt; uniform vec3 uCalm, uFoam, uFog;
        varying vec2 vUv; varying vec3 vW;
        ${NOISE_GLSL}
        void main(){
          vec2 p = (vUv - 0.5) * 2.0;
          float r = length(p);
          if (r > 1.0) discard;
          float ang = atan(p.y, p.x);
          // the sea inside is GLASS: a slow shimmer that fades to nothing at the centre
          float calm = smoothstep(0.95, 0.25, r) * (0.10 + 0.10 * vnoise(vec2(ang * 3.0, r * 6.0 - uTime * 0.25)));
          // the rim BOILS: a churned foam band where the calm meets the wall
          float band = smoothstep(0.86, 0.975, r) * smoothstep(1.0, 0.965, r);
          float churn = fbm2(vec2(ang * 9.0 + uTime * 0.6, r * 12.0));
          float foam = band * (0.35 + 0.65 * churn);
          vec3 col = mix(uCalm, uFoam, clamp(foam * 1.4, 0.0, 1.0));
          float a = (calm + foam * 0.7) * uAmt;
          if (a < 0.006) discard;
          float d = length(cameraPosition - vW);
          gl_FragColor = vec4(pow(mix(col, uFog, clamp(d / 900.0, 0.0, 0.75)), vec3(2.2)), a);
        }
      `,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
    });
    const pool = new THREE.Mesh(new THREE.PlaneGeometry(R * 2, R * 2), this.poolMat);
    pool.rotation.x = -Math.PI / 2; pool.position.y = 0.7; pool.renderOrder = 2;
    this.group.add(pool);
    // --- the eyewall: a towering ring of storm cloud, leaning outward as it climbs ---
    this.wallMat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uAmt: { value: 1 }, uDark: { value: new THREE.Color(0x3c4652) }, uLit: { value: new THREE.Color(0xb9c9d6) }, uFog: { value: new THREE.Color(0x8ec5e6) } },
      vertexShader: /* glsl */ `
        uniform float uTime;
        varying vec2 vUv; varying vec3 vN; varying vec3 vW;
        void main(){
          vUv = uv;
          vec3 p = position;
          p.xz *= 1.0 + uv.y * 0.55;                       // flares outward toward the top (a real eyewall)
          p.x += sin(uTime * 0.21 + uv.y * 2.0) * 6.0 * uv.y;
          vN = normalize(vec3(position.x, 0.0, position.z));
          vec4 wp = modelMatrix * vec4(p, 1.0);
          vW = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;   // uTime is shared with the vertex stage — precision must match
        uniform float uTime, uAmt; uniform vec3 uDark, uLit, uFog;
        varying vec2 vUv; varying vec3 vN; varying vec3 vW;
        ${NOISE_GLSL}
        void main(){
          float n = fbm3(vec2(vUv.x * 9.0 + uTime * 0.06, vUv.y * 2.6 - uTime * 0.22));
          float rain = vnoise(vec2(vUv.x * 40.0, vUv.y * 1.4 - uTime * 1.6));
          float body = smoothstep(0.34, 0.78, n * 0.8 + rain * 0.3);
          body *= smoothstep(0.0, 0.16, vUv.y) * smoothstep(1.0, 0.72, vUv.y);
          vec3 v = normalize(cameraPosition - vW);
          float rim = 1.0 - abs(dot(normalize(vN), v));
          float a = body * (0.26 + 0.62 * rim) * uAmt * 0.55;
          if (a < 0.006) discard;
          float d = length(cameraPosition - vW);
          gl_FragColor = vec4(pow(mix(mix(uDark, uLit, vUv.y * 0.75), uFog, clamp(d / 900.0, 0.0, 0.75)), vec3(2.2)), a);
        }
      `,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false,
    });
    const wall = new THREE.Mesh(new THREE.CylinderGeometry(R * 1.02, R * 1.02, 170, 40, 10, true), this.wallMat);
    wall.position.y = 85; wall.renderOrder = 3; wall.frustumCulled = false;
    this.group.add(wall);
    // --- the shaft of daylight down the middle: NO geometry edge, only a silhouette-weighted glow ---
    this.shaftMat = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uAmt: { value: 1 }, uColor: { value: new THREE.Color(0xcfeeff) } },
      vertexShader: 'varying vec2 vUv; varying vec3 vN; varying vec3 vW; void main(){ vUv = uv; vN = normalize(vec3(position.x, 0.0, position.z)); vec4 wp = modelMatrix * vec4(position, 1.0); vW = wp.xyz; gl_Position = projectionMatrix * viewMatrix * wp; }',
      fragmentShader: /* glsl */ `
        precision highp float;   // includes NOISE_GLSL — the cell hash must not be evaluated in fp16
        uniform float uTime, uAmt; uniform vec3 uColor;
        varying vec2 vUv; varying vec3 vN; varying vec3 vW;
        ${NOISE_GLSL}
        void main(){
          vec3 v = normalize(cameraPosition - vW);
          float rim = 1.0 - abs(dot(normalize(vN), v));          // brightest where you look THROUGH the most air
          float dust = 0.65 + 0.35 * fbm2(vec2(vUv.x * 5.0 + uTime * 0.05, vUv.y * 2.0 - uTime * 0.12));
          float vert = smoothstep(0.02, 0.45, vUv.y) * smoothstep(1.0, 0.5, vUv.y);
          float a = pow(rim, 2.6) * vert * dust * 0.15 * uAmt;
          if (a < 0.004) discard;
          gl_FragColor = vec4(pow(uColor, vec3(2.2)), a);
        }
      `,
      transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, fog: false,
    });
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(R * 0.30, R * 0.44, 210, 28, 8, true), this.shaftMat);
    shaft.position.y = 118; shaft.renderOrder = 4; shaft.frustumCulled = false;
    this.group.add(shaft);
    this.group.position.set(x, 0, z);
    scene.add(this.group);
  }

  update(t: number, night: number, storm: number, fog: THREE.Color): void {
    const amt = Math.max(0.25, Math.min(1, storm * 1.2));
    this.poolMat.uniforms.uTime!.value = t;
    this.poolMat.uniforms.uAmt!.value = amt;
    (this.poolMat.uniforms.uFog!.value as THREE.Color).copy(fog);
    this.wallMat.uniforms.uTime!.value = t;
    this.wallMat.uniforms.uAmt!.value = amt;
    (this.wallMat.uniforms.uFog!.value as THREE.Color).copy(fog);
    this.shaftMat.uniforms.uTime!.value = t;
    this.shaftMat.uniforms.uAmt!.value = amt * (1 - night * 0.85);   // by night it's moonlight, not a spotlight
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
    this.group.traverse((o) => { const m = o as THREE.Mesh; if (m.geometry) m.geometry.dispose(); });
    this.poolMat.dispose(); this.wallMat.dispose(); this.shaftMat.dispose();
  }
}
